import * as Sentry from '@sentry/react';
import type { Scope } from '@sentry/react';
import type { AnyAction } from '@reduxjs/toolkit';

import {
  CHAT_SLICE_NAME,
  CONFIG_SLICE_NAME,
  RESULTS_SLICE_NAME,
  SPECIALTIES_SLICE_NAME,
} from 'store/slices/slicesNames';

import { actions, select } from 'store/toolkit';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type RootState = any;

const actionsToIgnore = new Set<string>([
  actions.results.setHoveredResult.toString(),
  actions.results.clearHoveredResult.toString(),
  actions.map.moved.toString(),
]);

/**
 * functions for each slice that determine which values to include in the Sentry json file that is attached on errors
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type SliceTransformer = (slice: any) => any;

const sliceTransformers: Record<string, SliceTransformer> = {
  [CONFIG_SLICE_NAME]: ({ _values, ...rest }: Record<string, unknown>) => rest,
  [SPECIALTIES_SLICE_NAME]: ({ _specialties, _subspecialties, ...rest }: Record<string, unknown>) =>
    rest,
  [CHAT_SLICE_NAME]: ({ conversation: _conversation, ...rest }: Record<string, unknown>) => rest,

  [RESULTS_SLICE_NAME]: ({
    response: { _results, ...restResponse },
    ...resultsSlice
  }: {
    response: Record<string, unknown>;
    [key: string]: unknown;
  }) => ({
    ...resultsSlice,
    response: restResponse,
  }),
};

const sentryReduxEnhancer = Sentry.createReduxEnhancer({
  // https://docs.sentry.io/platforms/javascript/guides/react/features/redux/#redux-enhancer-options
  actionTransformer: ({
    type,
    meta,
    error,
  }: AnyAction): { type: string; meta?: unknown; error?: unknown } | null => {
    if (actionsToIgnore.has(type)) return null;
    return { type, meta, error }; // for now, ignore the payload to avoid sending too much data. Update this as necessary
  },

  stateTransformer: (state: RootState): RootState => {
    const transformedState = { ...state };

    Object.entries(sliceTransformers).forEach(([sliceName, transformer]) => {
      if (state[sliceName]) {
        transformedState[sliceName] = transformer(state[sliceName]);
      }
    });

    return transformedState;
  },

  configureScopeWithState: (scope: Scope, state: RootState): void => {
    const isClosedAccess: boolean = select.config.isClosedAccess(state);
    const hasAccessToken: boolean = select.session.hasAccessToken(state);
    const hasNetwork: boolean = Boolean(select.networks.currentSlug(state));
    const isLoggedIn: boolean = select.config.isLoggedIn(state);
    const configName: string | null = select.config.name(state);
    scope.setTag('isClosedAccess', isClosedAccess);
    scope.setTag('hasAccessToken', hasAccessToken);
    scope.setTag('hasNetwork', hasNetwork);
    scope.setTag('isLoggedIn', isLoggedIn);
    scope.setTag('configName', configName);
  },
});

export default sentryReduxEnhancer;
